import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Database,
  Zap,
  Globe,
  Shield,
  BarChart3,
  Users,
  ChevronDown,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { ContactModal } from '@/components/modals/ContactModal';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { useMarketingImages } from '@/hooks/useMarketingImages';

export default function DemoPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPurpose, setModalPurpose] = useState<string>();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const {
    getNavigationText,
    getDemoHero,
    getDemoBenefits,
    getDemoFeatures,
    getDemoFAQ,
    getDemoFinalCTA,
    getDemoFAQItems,
    getLandingFooter,
  } = useMarketingTranslations();
  const { getImage } = useMarketingImages();

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'KernLogic PIM Demo',
    description:
      'Interactive demonstration of enterprise PIM platform capabilities for manufacturers and retailers',
    url: 'https://kernlogic.com/demo',
    applicationCategory: 'ProductInformationManagement',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      name: 'Demo Request',
      url: 'https://kernlogic.com/demo',
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Demo',
        item: 'https://kernlogic.com/demo',
      },
    ],
  };

  const handleBookDemo = () => {
    setModalPurpose('Request a Demo');
    setIsModalOpen(true);
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Get FAQ items from translations
  const faqItems = getDemoFAQItems();

  // Benefits data
  const benefits = [
    {
      icon: Database,
      title: getDemoBenefits('benefit1.title'),
      description: getDemoBenefits('benefit1.description'),
    },
    {
      icon: Zap,
      title: getDemoBenefits('benefit2.title'),
      description: getDemoBenefits('benefit2.description'),
    },
    {
      icon: Globe,
      title: getDemoBenefits('benefit3.title'),
      description: getDemoBenefits('benefit3.description'),
    },
    {
      icon: Users,
      title: getDemoBenefits('benefit4.title'),
      description: getDemoBenefits('benefit4.description'),
    },
    {
      icon: BarChart3,
      title: getDemoBenefits('benefit5.title'),
      description: getDemoBenefits('benefit5.description'),
    },
  ];

  // Features data
  const features = [
    {
      icon: Database,
      title: getDemoFeatures('feature1.title'),
      description: getDemoFeatures('feature1.description'),
    },
    {
      icon: Globe,
      title: getDemoFeatures('feature2.title'),
      description: getDemoFeatures('feature2.description'),
    },
    {
      icon: Zap,
      title: getDemoFeatures('feature3.title'),
      description: getDemoFeatures('feature3.description'),
    },
    {
      icon: BarChart3,
      title: getDemoFeatures('feature4.title'),
      description: getDemoFeatures('feature4.description'),
    },
    {
      icon: Shield,
      title: getDemoFeatures('feature5.title'),
      description: getDemoFeatures('feature5.description'),
    },
    {
      icon: Users,
      title: getDemoFeatures('feature6.title'),
      description: getDemoFeatures('feature6.description'),
    },
  ];

  return (
    <main
      role="main"
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-scroll-container
    >
      {/* SEO Meta Tags */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>

      {/* Navigation */}
      <motion.header
        className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm"
        role="banner"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <nav
          className="container mx-auto px-6 py-4"
          role="navigation"
          aria-label={getNavigationText('ariaLabel')}
        >
          <div className="flex items-center justify-between">
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Link to="/marketing" className="group flex items-center">
                <motion.img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getNavigationText('brand')}
                  className="size-12 transition-transform group-hover:scale-105"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="ml-3 text-xl font-bold text-white">
                  {getNavigationText('brand')}
                </span>
              </Link>
            </motion.div>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Hero Section */}
      <motion.section
        className="relative overflow-hidden py-20 md:py-32"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        {/* Background Elements */}
        <motion.div
          className="absolute left-1/2 top-1/2 size-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500/10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <div className="container relative z-10 mx-auto px-6 text-center">
          <motion.h1
            className="mb-6 text-4xl font-bold leading-tight text-white md:text-6xl lg:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            {getDemoHero('headline')}
          </motion.h1>

          <motion.p
            className="mx-auto mb-12 max-w-4xl text-xl leading-relaxed text-slate-300 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            {getDemoHero('subheadline')}
          </motion.p>

          <motion.div
            className="flex flex-col items-center justify-center gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                onClick={handleBookDemo}
                size="lg"
                className="group h-auto bg-gradient-to-r from-primary-600 to-primary-500 px-12 py-6 text-xl font-semibold text-white shadow-xl transition-all duration-200 hover:from-primary-700 hover:to-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getDemoHero('cta')}
                <motion.div
                  className="ml-3 inline-block"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <ArrowRight
                    className="size-6 text-white transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </motion.div>
              </Button>
            </motion.div>
            <motion.p
              className="text-sm text-slate-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.3 }}
            >
              {getDemoHero('ctaSubtext')}
            </motion.p>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            className="mt-16 md:mt-20"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            <div className="relative mx-auto max-w-5xl">
              <div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/50 p-2 shadow-2xl backdrop-blur-sm">
                <img
                  src={getImage('searchFilter')}
                  alt="KernLogic Search & Filter Interface"
                  className="w-full rounded-xl"
                  loading="eager"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Key Benefits Section */}
      <motion.section
        className="relative py-24 md:py-32"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {getDemoBenefits('headline')}
            </h2>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/30 p-8 backdrop-blur-sm transition-all duration-300 hover:border-primary-500/50 hover:bg-slate-800/50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                >
                  <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-primary-500/20">
                    <Icon className="size-6 text-primary-400" />
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">{benefit.title}</h3>
                  <p className="text-slate-300">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Platform Capabilities Section */}
      <motion.section
        className="relative bg-slate-800/50 py-24 md:py-32"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {getDemoFeatures('headline')}
            </h2>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/30 p-8 backdrop-blur-sm transition-all duration-300 hover:border-primary-500/50 hover:bg-slate-900/50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                >
                  <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-primary-500/20">
                    <Icon className="size-6 text-primary-400" />
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">{feature.title}</h3>
                  <p className="text-slate-300">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        className="relative py-24 md:py-32"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {getDemoFAQ('headline')}
            </h2>
          </motion.div>

          <div className="mx-auto max-w-4xl">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                className="mb-4 overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-slate-800/50"
                >
                  <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                  <motion.div
                    animate={{ rotate: openFAQ === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="size-5 text-slate-400" />
                  </motion.div>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFAQ === index ? 'auto' : 0,
                    opacity: openFAQ === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-slate-700/50 p-6 pt-4">
                    <p className="text-slate-300">{item.answer}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Final CTA Section */}
      <motion.section
        className="relative bg-gradient-to-r from-primary-600 to-primary-500 py-24 md:py-32"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6 text-center">
          <motion.h2
            className="mb-6 text-4xl font-bold text-white md:text-5xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {getDemoFinalCTA('headline')}
          </motion.h2>

          <motion.p
            className="mx-auto mb-12 max-w-3xl text-xl text-primary-100 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {getDemoFinalCTA('subheadline')}
          </motion.p>

          <motion.div
            className="flex flex-col items-center justify-center gap-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                onClick={handleBookDemo}
                size="lg"
                className="group h-auto border border-slate-700 bg-slate-900 px-12 py-6 text-xl font-semibold text-white shadow-xl transition-all duration-200 hover:bg-slate-800 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 focus:ring-offset-primary-600"
              >
                {getDemoFinalCTA('cta')}
                <motion.div
                  className="ml-3 inline-block"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <ArrowRight
                    className="size-6 text-white transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </motion.div>
              </Button>
            </motion.div>

            <motion.p
              className="text-sm text-primary-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {getDemoFinalCTA('postscript')}
            </motion.p>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <motion.footer
        className="border-t border-slate-700/50 bg-slate-900 py-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <div className="mb-12 flex flex-col items-center justify-between md:flex-row">
            <div className="mb-8 md:mb-0">
              <Link to="/marketing" className="flex items-center">
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getNavigationText('brand')}
                  className="size-10"
                />
                <span className="ml-3 text-2xl font-bold text-white">
                  {getNavigationText('brand')}
                </span>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <Link
                to="/marketing"
                className="rounded-md px-4 py-2 text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                Back to Home
              </Link>
              <Link
                to="/marketing/pricing"
                className="rounded-md px-4 py-2 text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                View Pricing
              </Link>
            </div>
          </div>

          <div className="border-t border-slate-700/50 pt-8 text-center text-slate-500">
            <Link
              to="/privacy"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.privacy')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/terms"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.terms')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/cookies"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.cookies')}
            </Link>
          </div>
        </div>
      </motion.footer>

      {/* Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialPurpose={modalPurpose}
      />
    </main>
  );
}
